import React from "react";
import { Card, Col, Row } from "react-bootstrap";
import type { IconType } from "react-icons";
import { FaExclamationTriangle, FaUsers } from "react-icons/fa";
import type { OobEventDto } from "@/api/outOfBandApi";
import "./OobEventWorkspace.scss";

export interface OobEventMetric {
  label: string;
  value: React.ReactNode;
  icon: IconType;
  action?: React.ReactNode;
}

interface OobEventWorkspaceHeaderProps {
  event: OobEventDto;
  participantCount?: number;
  actions?: React.ReactNode;
  compact?: boolean;
}

const formatDate = (value?: string) => value ? new Date(value).toLocaleString() : "-";

export const formatDuration = (startedAt?: string) => {
  if (!startedAt) return "-";
  const normalizedStartedAt = /(?:Z|[+-]\d{2}:\d{2})$/i.test(startedAt) ? startedAt : `${startedAt}Z`;
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - new Date(normalizedStartedAt).getTime()) / 60000));
  if (elapsedMinutes < 1) return "Just started";
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

export const OobEventWorkspaceHeader: React.FC<OobEventWorkspaceHeaderProps> = ({ event, participantCount, actions, compact = false }) => {
  const startedAt = event.activatedDt || event.createDt;
  const isLocal = event.deliveryStatus === "LocalActive";
  const needsPivot = event.deliveryStatus === "PivotRequired";

  return (
    <Card className={`oob-event-header mb-3 ${compact ? "oob-event-header--compact" : ""}`}>
      <Card.Body className="d-flex flex-column flex-lg-row justify-content-between gap-3">
        <div className="min-w-0">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <h5 className="mb-0 text-break">{event.eventName}</h5>
            <span className={`oob-event-chip oob-event-chip--${event.status.toLowerCase()}`} role="status">{event.status}</span>
            <span className="oob-event-chip oob-event-chip--type">{event.eventType}</span>
            {isLocal && <span className="oob-event-chip oob-event-chip--local">Local Communication Space</span>}
            {needsPivot && <span className="oob-event-chip oob-event-chip--pivot">Local Pivot Required</span>}
          </div>
          {event.description && <div className="oob-event-header__description small text-muted mb-1">{event.description}</div>}
          <div className="small text-muted d-flex flex-wrap gap-3">
            <span>Started {formatDate(startedAt)}</span>
            {participantCount !== undefined && <span><FaUsers className="me-1" aria-hidden="true" />{participantCount} participants</span>}
            {isLocal && <span>Local communication space (Teams unavailable)</span>}
          </div>
        </div>
        {actions && <div className="d-flex flex-wrap gap-2 align-content-start">{actions}</div>}
      </Card.Body>
    </Card>
  );
};

export const OobDeliveryStatusBanner: React.FC<{ event: OobEventDto }> = ({ event }) => {
  if (event.deliveryStatus !== "PivotRequired") return null;

  return (
    <div className="oob-delivery-banner oob-delivery-banner--pivot" role="status">
      <FaExclamationTriangle aria-hidden="true" />
      <div>
        <strong>Teams provisioning needs a local pivot</strong>
        <div className="small">
          {event.deliveryError || "Teams could not be provisioned. Review the event details, then activate the local communication space."}
        </div>
      </div>
    </div>
  );
};

export const OobEventMetricStrip: React.FC<{ metrics: OobEventMetric[]; compact?: boolean }> = ({ metrics, compact = false }) => (
  <Row className={`g-2 oob-event-metric-strip mb-3 ${compact ? "oob-event-metric-strip--compact" : ""}`}>
    {metrics.map((metric) => {
      const Icon = metric.icon;
      return (
        <Col key={metric.label} xs={6} md={4} xl>
          <Card className="h-100 oob-event-metric">
            <Card.Body className="d-flex gap-3 align-items-start">
              <Icon className="oob-event-metric__icon" aria-hidden="true" />
              <div className="min-w-0">
                <div className="oob-event-metric__value">{metric.value}</div>
                <div className="oob-event-metric__label">
                  <span>{metric.label}</span>
                  {metric.action}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      );
    })}
  </Row>
);